///
/// EarthBrowser
///
/// Created by Matt Giger on 11/4/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Proactive memory watch for Chromium. performance.measureUserAgentSpecificMemory()
/// reports the page's own memory (JS heaps, DOM, workers - not GPU
/// textures, which TextureMemory already counts) and is available because
/// the app is cross-origin isolated (COOP/COEP in public/_headers). On
/// memory-constrained devices a reading over the threshold switches the
/// session to lite mode before the OS gets to decide, which is the only
/// pre-kill signal any browser offers us; WebKit has no equivalent and
/// stays on the crash-sentinel path alone.
///
/// The browser throttles the call (it resolves after a randomized delay,
/// often tens of seconds) and samples at most a few times a minute, so the
/// loop is "measure, then wait, then measure" rather than a timer.

import { recordCrumb } from "@/earthbrowser/utils/CrashSentinel"
import { isLiteMode, setLiteMode } from "@/earthbrowser/utils/MemoryBudget"
import { isMemoryConstrainedDevice } from "@/earthbrowser/utils/MemoryLog"
import { track } from "@/utils/analytics"

const cMB = 1024 * 1024
/** Page memory (excluding GPU) past which a constrained device goes lite.
 *  Android kills a foreground renderer somewhere past ~1 GB total; with a
 *  few hundred MB of textures beside it, half a GB of heap is the limit */
const cConstrainedThresholdBytes = 512 * cMB
/** Pause between samples beyond the browser's own throttling */
const cSampleGapMs = 15_000
const cMaxSamples = 40

interface MemoryMeasurement {
	bytes: number
}

type MeasureFn = () => Promise<MemoryMeasurement>

let supported = false
let running = false
let tripped = false
let lastBytes = 0
let lastAt = 0
const samples: number[] = []

function measureFn(): MeasureFn | null {
	if (typeof window === "undefined" || !window.crossOriginIsolated) return null
	const perf = performance as Performance & { measureUserAgentSpecificMemory?: MeasureFn }
	return typeof perf.measureUserAgentSpecificMemory === "function"
		? perf.measureUserAgentSpecificMemory.bind(perf)
		: null
}

function sleep(ms: number): Promise<void> {
	return new Promise(resolve => window.setTimeout(resolve, ms))
}

async function loop(measure: MeasureFn): Promise<void> {
	while (running) {
		try {
			const { bytes } = await measure()
			lastBytes = bytes
			lastAt = Date.now()
			samples.push(bytes)
			if (samples.length > cMaxSamples) samples.splice(0, samples.length - cMaxSamples)
			const mb = Math.round(bytes / cMB)
			recordCrumb(`mem:page=${mb}MB`)

			if (!tripped && isMemoryConstrainedDevice() && bytes > cConstrainedThresholdBytes && !isLiteMode()) {
				tripped = true
				console.warn(`[MemoryWatch] page memory ${mb}MB over ${Math.round(cConstrainedThresholdBytes / cMB)}MB; entering lite mode`)
				recordCrumb(`mem:lite page=${mb}MB`)
				setLiteMode(true)
				track("lite_mode_entered", { source: "memory_watch", mb })
			}
		} catch {
			// SecurityError when isolation is lost mid-session, or the
			// measurement was aborted: stop quietly
			running = false
			return
		}
		await sleep(cSampleGapMs)
	}
}

/** Start sampling if the browser supports it. Safe to call once at boot. */
export function startMemoryWatch(): void {
	if (running) return
	const measure = measureFn()
	supported = measure !== null
	if (!measure) return
	running = true
	void loop(measure)
}

/** Diagnostic snapshot for window.__ebDebug */
export function memoryWatchSnapshot(): Record<string, unknown> {
	return {
		supported,
		running,
		tripped,
		thresholdMb: Math.round(cConstrainedThresholdBytes / cMB),
		constrained: isMemoryConstrainedDevice(),
		lastMb: Math.round(lastBytes / cMB),
		lastAt,
		samplesMb: samples.map(b => Math.round(b / cMB)),
	}
}
